/**
 * Kebidanan clinical calculators.
 * Uses native Date (no dayjs/moment dependency in emedika package.json).
 */

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function formatYmd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseYmd(s: string): Date | null {
  if (!s) return null;
  // Accept YYYY-MM-DD or ISO; use only the date portion to avoid TZ drift.
  const datePart = s.length >= 10 ? s.slice(0, 10) : s;
  const parts = datePart.split("-");
  if (parts.length !== 3) return null;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  if (
    Number.isNaN(y) ||
    Number.isNaN(m) ||
    Number.isNaN(d) ||
    m < 1 ||
    m > 12 ||
    d < 1 ||
    d > 31
  ) {
    return null;
  }
  return new Date(y, m - 1, d);
}

/**
 * Taksiran Persalinan (Naegele's rule):
 *   TP = HPHT + 7 hari - 3 bulan + 1 tahun.
 * Input HPHT string `YYYY-MM-DD`, output `YYYY-MM-DD` or null when invalid.
 */
export function calculateTp(hpht: string): string | null {
  const d = parseYmd(hpht);
  if (!d) return null;
  // +7 days first
  d.setDate(d.getDate() + 7);
  // -3 months, +1 year == +9 months
  d.setMonth(d.getMonth() + 9);
  return formatYmd(d);
}

/**
 * Taksiran Persalinan dengan koreksi siklus haid ≠ 28 hari (modified Naegele):
 *   TP adjusted = TP Naegele + (siklus − 28) hari.
 * Bila siklus tidak diisi/28, hasilnya sama dengan calculateTp.
 */
export function calculateTpAdjusted(
  hpht: string,
  siklus?: number | null
): string | null {
  const base = calculateTp(hpht);
  if (!base) return null;
  const s = typeof siklus === "number" && siklus > 0 ? siklus : 28;
  if (s === 28) return base;
  const d = parseYmd(base);
  if (!d) return base;
  d.setDate(d.getDate() + (s - 28));
  return formatYmd(d);
}

/**
 * Usia kehamilan in weeks+days between HPHT and reference date (default today).
 */
export function calculateUsiaKehamilan(
  hpht: string,
  ref?: string
): { minggu: number; hari: number } | null {
  const start = parseYmd(hpht);
  if (!start) return null;
  const refDate = ref ? parseYmd(ref) : new Date();
  if (!refDate) return null;
  // Normalize refDate to midnight so diff is whole days.
  const refMid = new Date(
    refDate.getFullYear(),
    refDate.getMonth(),
    refDate.getDate()
  );
  const days = Math.floor(
    (refMid.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (days < 0) return { minggu: 0, hari: 0 };
  return { minggu: Math.floor(days / 7), hari: days % 7 };
}

/**
 * Trimester berdasarkan usia kehamilan (minggu):
 *   T1: 0–13 mg+6hr, T2: 14–27 mg+6hr, T3: ≥ 28 mg.
 */
export function calculateTrimester(ukMinggu: number | null): string | null {
  if (ukMinggu == null || ukMinggu < 0) return null;
  if (ukMinggu < 14) return "Trimester I";
  if (ukMinggu < 28) return "Trimester II";
  return "Trimester III";
}

/**
 * Status kehamilan berdasarkan UK saat ini:
 *   Preterm < 37 mg, Aterm 37–41 mg, Post-term ≥ 42 mg.
 */
export function calculateStatusKehamilan(
  ukMinggu: number | null
): string | null {
  if (ukMinggu == null || ukMinggu < 0) return null;
  if (ukMinggu < 37) return "Preterm";
  if (ukMinggu < 42) return "Aterm";
  return "Post-term";
}

/**
 * Sisa hari menuju HPL/TP dari tanggal referensi (default hari ini).
 * Positif = masih menuju HPL; negatif = sudah melewati HPL.
 */
export function calculateSisaHari(
  hpl: string,
  ref?: string
): number | null {
  const target = parseYmd(hpl);
  if (!target) return null;
  const refDate = ref ? parseYmd(ref) : new Date();
  if (!refDate) return null;
  const refMid = new Date(
    refDate.getFullYear(),
    refDate.getMonth(),
    refDate.getDate()
  );
  return Math.round(
    (target.getTime() - refMid.getTime()) / (1000 * 60 * 60 * 24)
  );
}

/**
 * Mean Arterial Pressure: (Sistole + 2 × Diastole) / 3, dibulatkan integer.
 */
export function calculateMap(
  sistole: number | null,
  diastole: number | null
): number | null {
  if (sistole == null || diastole == null || sistole <= 0 || diastole <= 0) {
    return null;
  }
  return Math.round((sistole + 2 * diastole) / 3);
}

/**
 * Taksiran Berat Janin (Rumus Johnson-Toshack):
 *   TBJ = (TFU - n) * 155 gram,
 *   n = 12 (kepala sudah masuk PAP) atau 11 (belum).
 */
export function calculateTbj(
  tfuCm: number | null,
  sudahPap: boolean
): number | null {
  if (tfuCm == null || Number.isNaN(tfuCm) || tfuCm <= 0) return null;
  const n = sudahPap ? 12 : 11;
  return Math.round((tfuCm - n) * 155);
}

/**
 * Indeks Massa Tubuh: BB (kg) / (TB (m))^2, dibulatkan 2 desimal.
 */
export function calculateImt(
  bbKg: number | null,
  tbCm: number | null
): number | null {
  if (bbKg == null || tbCm == null || tbCm <= 0) return null;
  const tbM = tbCm / 100;
  return Math.round((bbKg / (tbM * tbM)) * 100) / 100;
}

/**
 * Kenaikan BB selama kehamilan (kg), dibulatkan 2 desimal.
 */
export function calculateKenaikanBb(
  bbSekarang: number | null,
  bbPra: number | null
): number | null {
  if (bbSekarang == null || bbPra == null) return null;
  return Math.round((bbSekarang - bbPra) * 100) / 100;
}
